import { RecordingQuality, FacingMode } from '../types/media.enums';
import { RecordingOptions } from '../types/recordrtc.types';
import { isMobile, isIOS } from './browser-detection';

/**
 * Quality settings for video recording - higher quality defaults
 */
const QUALITY_SETTINGS = {
  [RecordingQuality.LOW]: { width: 640, height: 480, frameRate: 20 },
  [RecordingQuality.MEDIUM]: { width: 1280, height: 720, frameRate: 25 },
  [RecordingQuality.HIGH]: { width: 1280, height: 720, frameRate: 30 },
  [RecordingQuality.ULTRA]: { width: 1920, height: 1080, frameRate: 30 },
};

/**
 * Create video constraints for getUserMedia
 */
export const createVideoConstraints = (options: RecordingOptions = {}) => {
  const settings = QUALITY_SETTINGS[options.quality || RecordingQuality.MEDIUM];

  const isOnMobile = isMobile();

  // Mobile (iOS & Android): use minimal constraints (aspectRatio causes zoom issues)
  // Desktop: use aspectRatio + ideal dimensions for landscape
  let finalSettings;

  if (isOnMobile) {
    finalSettings = {
      frameRate: settings.frameRate,
    };
  } else {
    finalSettings = {
      width: { ideal: settings.width },
      height: { ideal: settings.height },
      aspectRatio: 16 / 9, // Ensure landscape on desktop
      frameRate: settings.frameRate,
    };
  }

  // Prefer deviceId over cameraId, then fallback to facingMode
  if (options.deviceId) {
    return { deviceId: { exact: options.deviceId }, ...finalSettings };
  }

  if (options.cameraId) {
    return { deviceId: { exact: options.cameraId }, ...finalSettings };
  }

  return {
    facingMode: options.facingMode || FacingMode.USER,
    ...finalSettings,
  };
};

/**
 * Create audio constraints for getUserMedia
 */
export const createAudioConstraints = (enableAudio: boolean = true) => {
  if (!enableAudio) return false;

  return {
    echoCancellation: true,
    noiseSuppression: true,
    sampleRate: 44100,
  };
};
